import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Layout from './components/Layout';
import Dashboard from './components/dashboard/Dashboard';
import ToastContainer from './components/ui/toast';
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { RequestPasswordReset, ResetPassword } from './components/auth/PasswordReset';
import { EmailVerification } from './components/auth/EmailVerification';
import ProfilePage from './pages/ProfilePage';
import { useEffect } from 'react';

declare global {
  interface Window {
    clearQueryCache?: () => void;
  }
}

// Create the query client instance outside of components with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000,        // Consider data fresh for 60 seconds
      gcTime: 1000 * 60 * 5,   // Keep unused data in cache for 5 minutes
      retry: 1,                // Only retry failed requests once
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnReconnect: false,   // Don't refetch on reconnect
    },
  },
});

// Set up global access to query client for logout
window.clearQueryCache = () => {
  queryClient.clear();
};

// Route logger component
const RouteLogger = () => {
  const location = useLocation();
  
  useEffect(() => {
    console.log('📍 App Router - URL changed to:', location.pathname + location.search);
  }, [location]);
  
  return null;
};

// Root App component
const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <RouteLogger />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route path="/reset-password" element={<RequestPasswordReset />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            
            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProfilePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
          <ToastContainer />
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default App;